import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────────
   SolveSmart — Dark UI, scroll-driven animations, anime.js aesthetic
   Palette: #0a0a0f bg · #e8ff47 acid-yellow accent · #7c3aed purple
   Type: Syne (display) + Inter (body) + JetBrains Mono (code/labels)
───────────────────────────────────────────────────────────────── */

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg:       #07070d;
    --bg1:      #0e0e18;
    --bg2:      #13131f;
    --border:   rgba(255,255,255,0.06);
    --text:     #e8e8f0;
    --muted:    #f2f2f5;
    --accent:   #e8ff47;
    --purple:   #8b5cf6;
    --pink:     #f472b6;
    --cyan:     #22d3ee;
    --red:      #ff4d6d;
    --radius:   14px;
  }

  html { scroll-behavior: smooth; }
  body { background: var(--bg); }

  .ss { font-family: 'Inter', sans-serif; background: var(--bg); color: var(--text); overflow-x: hidden; }

  /* ── NOISE OVERLAY ── */
  .ss::before {
    content: '';
    position: fixed; inset: 0; z-index: 0; pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    opacity: 0.4;
  }

  /* ── NAV ── */
  .ss-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 500;
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 48px; height: 64px;
    background: rgba(7,7,13,0.75);
    backdrop-filter: blur(20px) saturate(1.4);
    border-bottom: 1px solid var(--border);
    transition: background .3s;
  }
  .ss-nav-logo {
    font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 800;
    color: var(--text); letter-spacing: -0.4px; display: flex; align-items: center; gap: 10px;
    cursor: pointer;
  }
  .ss-nav-logo-mark {
    width: 26px; height: 26px; border-radius: 6px;
    background: var(--accent); display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 800; color: var(--bg); line-height: 1;
  }
  .ss-nav-links { display: flex; align-items: center; gap: 36px; list-style: none; }
  .ss-nav-links a {
    font-size: 13px; font-weight: 500; color: var(--muted); text-decoration: none;
    letter-spacing: 0.01em; transition: color .2s;
  }
  .ss-nav-links a:hover { color: var(--text); }
  .ss-nav-right { display: flex; gap: 10px; align-items: center; }
  .ss-nav-ghost {
    font-size: 13px; font-weight: 500; color: var(--muted); background: none;
    border: 1px solid var(--border); padding: 7px 16px; border-radius: 8px;
    cursor: pointer; font-family: inherit; transition: all .2s;
  }
  .ss-nav-ghost:hover { border-color: rgba(255,255,255,0.15); color: var(--text); }
  .ss-nav-cta {
    font-size: 13px; font-weight: 600; background: var(--accent); color: var(--bg);
    border: none; padding: 8px 18px; border-radius: 8px; cursor: pointer;
    font-family: inherit; transition: all .2s; letter-spacing: -0.01em;
  }
  .ss-nav-cta:hover { background: #f5ff70; transform: translateY(-1px); }

  /* ── HERO ── */
  .ss-hero {
    position: relative; min-height: 100vh;
    display: flex; flex-direction: column; justify-content: center;
    padding: 120px 48px 80px;
    overflow: hidden;
  }
  .ss-hero-bg {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
  }
  .ss-hero-glow-1 {
    position: absolute; top: -200px; left: -200px;
    width: 700px; height: 700px; border-radius: 50%;
    background: radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%);
    animation: glow-drift-1 12s ease-in-out infinite alternate;
  }
  .ss-hero-glow-2 {
    position: absolute; top: 100px; right: -150px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(232,255,71,0.1) 0%, transparent 70%);
    animation: glow-drift-2 9s ease-in-out infinite alternate;
  }
  .ss-hero-glow-3 {
    position: absolute; bottom: 0; left: 30%;
    width: 600px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(244,114,182,0.08) 0%, transparent 70%);
    animation: glow-drift-3 14s ease-in-out infinite alternate;
  }
  @keyframes glow-drift-1 { from { transform: translate(0,0) scale(1); } to { transform: translate(80px,60px) scale(1.15); } }
  @keyframes glow-drift-2 { from { transform: translate(0,0); } to { transform: translate(-60px,80px); } }
  @keyframes glow-drift-3 { from { transform: translate(0,0); } to { transform: translate(40px,-40px); } }

  .ss-hero-inner {
    position: relative; z-index: 1;
    max-width: 1160px; margin: 0 auto; width: 100%;
    display: grid; grid-template-columns: 1fr 480px; gap: 80px; align-items: center;
  }
  .ss-hero-eyebrow {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px; font-weight: 500; color: var(--accent);
    letter-spacing: 0.14em; text-transform: uppercase;
    margin-bottom: 22px; display: flex; align-items: center; gap: 10px;
  }
  .ss-hero-eyebrow::before {
    content: ''; width: 24px; height: 1px; background: var(--accent); display: inline-block;
  }
  .ss-hero-h1 {
    font-family: 'Syne', sans-serif;
    font-size: clamp(48px, 6vw, 88px);
    font-weight: 800; line-height: 0.95; letter-spacing: -3px;
    color: var(--text); margin-bottom: 28px;
  }
  .ss-hero-h1 .line { display: block; overflow: hidden; }
  .ss-hero-h1 .line-inner { display: block; }
  .ss-hero-h1 .accent { color: var(--accent); }
  .ss-hero-h1 .outline {
    -webkit-text-stroke: 1.5px rgba(232,255,71,0.4);
    color: transparent;
  }
  .ss-hero-sub {
    font-size: 16px; line-height: 1.7; color: var(--muted); max-width: 460px; margin-bottom: 44px;
  }
  .ss-hero-actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 60px; }
  .ss-btn-primary {
    font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 600;
    background: var(--accent); color: var(--bg);
    border: none; padding: 14px 28px; border-radius: 10px; cursor: pointer;
    display: flex; align-items: center; gap: 8px;
    transition: all .25s cubic-bezier(.16,1,.3,1);
    letter-spacing: 0.02em;
  }
  .ss-btn-primary:hover { background: #f5ff70; transform: translateY(-3px); box-shadow: 0 12px 32px rgba(232,255,71,0.22); }
  .ss-btn-ghost {
    font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 500;
    background: transparent; color: var(--muted);
    border: 1px solid var(--border); padding: 14px 28px; border-radius: 10px; cursor: pointer;
    transition: all .25s; letter-spacing: 0.02em;
  }
  .ss-btn-ghost:hover { border-color: rgba(255,255,255,0.15); color: var(--text); transform: translateY(-3px); }
  .ss-hero-stats { display: flex; gap: 40px; }
  .ss-stat-item { display: flex; flex-direction: column; gap: 2px; }
  .ss-stat-num {
    font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800;
    color: var(--text); letter-spacing: -1px; line-height: 1;
  }
  .ss-stat-num em { font-style: normal; color: var(--accent); }
  .ss-stat-label { font-size: 11px; color: var(--muted); font-weight: 500; letter-spacing: 0.04em; }

  /* ── HERO RIGHT — Terminal Card ── */
  .ss-hero-terminal {
    background: var(--bg1); border: 1px solid var(--border);
    border-radius: var(--radius); overflow: hidden;
    box-shadow: 0 32px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.03);
  }
  .ss-terminal-bar {
    padding: 14px 20px; background: var(--bg2);
    border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 8px;
  }
  .ss-t-dot { width: 10px; height: 10px; border-radius: 50%; }
  .ss-terminal-body {
    padding: 24px; font-family: 'JetBrains Mono', monospace; font-size: 12px;
    line-height: 1.9; color: var(--muted); min-height: 320px;
    position: relative;
  }
  .ss-t-line { display: flex; gap: 12px; }
  .ss-t-prompt { color: var(--accent); user-select: none; }
  .ss-t-cmd { color: var(--cyan); }
  .ss-t-arg { color: var(--purple); }
  .ss-t-out { color: var(--muted); padding-left: 20px; }
  .ss-t-success { color: #4ade80; padding-left: 20px; }
  .ss-t-cursor {
    display: inline-block; width: 8px; height: 14px;
    background: var(--accent); animation: blink .9s step-end infinite; vertical-align: middle;
    margin-left: 2px;
  }
  @keyframes blink { 50% { opacity: 0; } }
  .ss-terminal-canvas { width: 100%; height: 180px; display: block; background: var(--bg); }

  /* ── MARQUEE ── */
  .ss-marquee-wrap {
    border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
    padding: 14px 0; overflow: hidden; background: var(--bg1);
    position: relative; z-index: 1;
  }
  .ss-marquee-track {
    display: flex; gap: 0; animation: marquee 22s linear infinite;
    width: max-content;
  }
  @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
  .ss-marquee-item {
    font-family: 'JetBrains Mono', monospace; font-size: 11px;
    color: var(--muted); letter-spacing: 0.08em; white-space: nowrap;
    padding: 0 32px; display: flex; align-items: center; gap: 12px;
  }
  .ss-marquee-item::after { content: '//'; color: var(--accent); }

  /* ── SECTION BASE ── */
  .ss-section { padding: 120px 48px; position: relative; z-index: 1; }
  .ss-container { max-width: 1160px; margin: 0 auto; }
  .ss-eyebrow {
    font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600;
    color: var(--accent); letter-spacing: 0.14em; text-transform: uppercase;
    margin-bottom: 18px; display: flex; align-items: center; gap: 10px;
  }
  .ss-eyebrow::before { content: ''; width: 16px; height: 1px; background: var(--accent); }
  .ss-h2 {
    font-family: 'Syne', sans-serif; font-size: clamp(36px, 4.5vw, 64px);
    font-weight: 800; letter-spacing: -2.5px; color: var(--text);
    line-height: 1.0; margin-bottom: 16px;
  }
  .ss-lead { font-size: 16px; color: var(--muted); line-height: 1.7; max-width: 480px; }

  /* ── SCROLL PROGRESS LINE ── */
  .ss-progress-bar {
    position: fixed; top: 0; left: 0; height: 2px; z-index: 1000;
    background: linear-gradient(90deg, var(--purple), var(--accent));
    transition: width .1s linear;
  }

  /* ── FEATURES — PIN SCROLL ── */
  .ss-features-sticky { position: sticky; top: 80px; }
  .ss-features-layout {
    display: grid; grid-template-columns: 1fr 1fr; gap: 0;
    border: 1px solid var(--border); border-radius: 16px; overflow: hidden;
    background: var(--bg1);
  }
  .ss-features-nav { border-right: 1px solid var(--border); }
  .ss-feat-tab {
    padding: 28px 32px; border-bottom: 1px solid var(--border); cursor: pointer;
    transition: all .25s; position: relative; overflow: hidden;
  }
  .ss-feat-tab:last-child { border-bottom: none; }
  .ss-feat-tab::before {
    content: ''; position: absolute; left: 0; top: 0; bottom: 0;
    width: 2px; background: var(--accent);
    transform: scaleY(0); transition: transform .3s cubic-bezier(.16,1,.3,1);
    transform-origin: bottom;
  }
  .ss-feat-tab.active::before { transform: scaleY(1); }
  .ss-feat-tab.active { background: rgba(232,255,71,0.04); }
  .ss-feat-tab-num {
    font-family: 'JetBrains Mono', monospace; font-size: 10px;
    color: var(--muted); margin-bottom: 8px; letter-spacing: 0.1em;
  }
  .ss-feat-tab.active .ss-feat-tab-num { color: var(--accent); }
  .ss-feat-tab-title {
    font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 800;
    color: var(--muted); letter-spacing: -0.4px; transition: color .25s;
  }
  .ss-feat-tab.active .ss-feat-tab-title { color: var(--text); }
  .ss-feat-panel { padding: 48px; display: none; flex-direction: column; justify-content: center; }
  .ss-feat-panel.active { display: flex; animation: fade-in-up .4s cubic-bezier(.16,1,.3,1); }
  @keyframes fade-in-up {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: none; }
  }
  .ss-feat-icon { font-size: 48px; margin-bottom: 24px; display: block; }
  .ss-feat-title {
    font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800;
    color: var(--text); letter-spacing: -1px; margin-bottom: 14px;
  }
  .ss-feat-desc { font-size: 15px; color: var(--muted); line-height: 1.75; margin-bottom: 28px; }
  .ss-feat-tag {
    font-family: 'JetBrains Mono', monospace; font-size: 10px;
    background: rgba(139,92,246,0.15); color: var(--purple);
    border: 1px solid rgba(139,92,246,0.2); padding: 5px 12px; border-radius: 20px;
    display: inline-flex; align-items: center; gap: 6px; width: fit-content;
  }
  .ss-feat-canvas { width: 100%; height: 180px; border-radius: 10px; overflow: hidden; margin-top: 24px; background: var(--bg); }

  /* ── CARDS GRID ── */
  .ss-cards {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 64px;
  }
  .ss-card {
    background: var(--bg1);
    border: 1px solid var(--border); border-radius: 16px;
    overflow: hidden; position: relative;
    transition: border-color .3s, transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s;
    display: flex; flex-direction: column;
  }
  .ss-card:hover {
    border-color: rgba(255,255,255,0.12);
    transform: translateY(-6px);
    box-shadow: 0 24px 60px rgba(0,0,0,0.45);
  }
  /* image area */
  .ss-card-img {
    width: 100%; height: 180px; position: relative; overflow: hidden; flex-shrink: 0;
  }
  .ss-card-img img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform .55s cubic-bezier(.16,1,.3,1);
    display: block;
  }
  .ss-card:hover .ss-card-img img { transform: scale(1.07); }
  /* dark overlay + coloured tint on hover */
  .ss-card-img::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(7,7,13,0.1) 0%, rgba(7,7,13,0.72) 100%);
    transition: opacity .3s;
  }
  /* floating icon badge over the image */
  .ss-card-img-badge {
    position: absolute; bottom: 14px; left: 16px; z-index: 2;
    width: 40px; height: 40px; border-radius: 10px;
    background: rgba(7,7,13,0.7); backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.1);
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
  }
  /* accent stripe that appears on hover */
  .ss-card-accent-bar {
    position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--purple), var(--accent));
    transform: scaleX(0); transform-origin: left;
    transition: transform .4s cubic-bezier(.16,1,.3,1);
  }
  .ss-card:hover .ss-card-accent-bar { transform: scaleX(1); }
  /* body */
  .ss-card-body { padding: 24px 28px 28px; flex: 1; }
  .ss-card-n {
    font-family: 'JetBrains Mono', monospace; font-size: 10px;
    color: var(--muted); margin-bottom: 10px; letter-spacing: 0.12em;
  }
  .ss-card-title {
    font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 800;
    letter-spacing: -0.4px; color: var(--text); margin-bottom: 8px; line-height: 1.2;
  }
  .ss-card-desc { font-size: 13px; color: var(--muted); line-height: 1.7; }
  .ss-card-tag {
    margin-top: 16px; display: inline-flex; align-items: center; gap: 6px;
    font-family: 'JetBrains Mono', monospace; font-size: 10px;
    color: var(--accent); letter-spacing: 0.08em;
  }
  .ss-card-tag::before { content: '→'; }

  /* ── BIG NUMBER BAND ── */
  .ss-band {
    background: var(--bg1); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
    padding: 64px 48px;
  }
  .ss-band-inner {
    max-width: 1160px; margin: 0 auto;
    display: grid; grid-template-columns: repeat(4, 1fr);
    gap: 0;
  }
  .ss-band-stat {
    padding: 0 40px; border-right: 1px solid var(--border); text-align: center;
  }
  .ss-band-stat:first-child { padding-left: 0; text-align: left; }
  .ss-band-stat:last-child { border-right: none; }
  .ss-band-n {
    font-family: 'Syne', sans-serif; font-size: 56px; font-weight: 800;
    letter-spacing: -3px; line-height: 1;
    background: linear-gradient(135deg, var(--text) 0%, var(--accent) 100%);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    margin-bottom: 8px;
  }
  .ss-band-label { font-size: 13px; color: var(--muted); font-weight: 500; }

  /* ── CODE WINDOW ── */
  .ss-code-win {
    background: #0d0d18; border: 1px solid var(--border); border-radius: 14px; overflow: hidden;
    margin-top: 64px; box-shadow: 0 40px 80px rgba(0,0,0,0.4);
  }
  .ss-code-topbar {
    padding: 14px 20px; background: var(--bg2); border-bottom: 1px solid var(--border);
    display: flex; align-items: center; gap: 8px;
  }
  .ss-code-tab {
    font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--muted);
    padding: 4px 14px; border-radius: 5px; background: transparent;
  }
  .ss-code-tab.act { background: var(--bg1); color: var(--accent); border: 1px solid var(--border); }
  .ss-code-body {
    display: grid; grid-template-columns: 1fr 1fr;
  }
  .ss-code-editor {
    padding: 28px; border-right: 1px solid var(--border);
    font-family: 'JetBrains Mono', monospace; font-size: 12px;
    line-height: 1.9; color: var(--muted); min-height: 280px;
  }
  .ss-code-preview {
    padding: 28px; display: flex; align-items: center; justify-content: center;
    background: var(--bg1); position: relative; overflow: hidden;
  }
  .ck { color: #ff9757; }
  .cf { color: var(--cyan); }
  .cs { color: #a8ff78; }
  .cn { color: #ffcc66; }
  .cp { color: var(--purple); }
  .cm { color: #555; font-style: italic; }

  /* ── STEPS ── */
  .ss-steps {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 16px; margin-top: 64px;
  }
  .ss-step {
    background: var(--bg1); border: 1px solid var(--border); border-radius: 16px;
    overflow: hidden; display: flex; flex-direction: column; position: relative;
    transition: border-color .3s, transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s;
  }
  .ss-step:hover {
    border-color: rgba(255,255,255,0.12); transform: translateY(-6px);
    box-shadow: 0 24px 60px rgba(0,0,0,0.45);
  }
  /* accent bar on top */
  .ss-step-accent-bar {
    position: absolute; top: 0; left: 0; right: 0; height: 2px;
    transform: scaleX(0); transform-origin: left;
    transition: transform .4s cubic-bezier(.16,1,.3,1);
  }
  .ss-step:hover .ss-step-accent-bar { transform: scaleX(1); }
  /* image */
  .ss-step-img {
    width: 100%; height: 190px; position: relative; overflow: hidden; flex-shrink: 0;
  }
  .ss-step-img img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform .55s cubic-bezier(.16,1,.3,1); display: block;
  }
  .ss-step:hover .ss-step-img img { transform: scale(1.07); }
  .ss-step-img::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(7,7,13,0.05) 0%, rgba(7,7,13,0.75) 100%);
  }
  /* step number badge over image */
  .ss-step-badge {
    position: absolute; top: 14px; left: 16px; z-index: 2;
    font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 600;
    color: var(--accent); letter-spacing: 0.12em;
    background: rgba(7,7,13,0.72); backdrop-filter: blur(10px);
    border: 1px solid rgba(232,255,71,0.2); padding: 5px 10px; border-radius: 6px;
  }
  /* icon badge bottom-left of image */
  .ss-step-img-icon {
    position: absolute; bottom: 14px; left: 16px; z-index: 2;
    width: 42px; height: 42px; border-radius: 10px;
    background: rgba(7,7,13,0.72); backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.1);
    display: flex; align-items: center; justify-content: center; font-size: 20px;
  }
  /* body */
  .ss-step-body { padding: 24px 28px 28px; flex: 1; }
  .ss-step-title {
    font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 800;
    letter-spacing: -0.4px; color: var(--text); margin-bottom: 10px;
  }
  .ss-step-desc { font-size: 13px; color: var(--muted); line-height: 1.75; }
  .ss-step-cta {
    margin-top: 18px; font-family: 'JetBrains Mono', monospace; font-size: 10px;
    color: var(--accent); letter-spacing: 0.08em; display: flex; align-items: center; gap: 6px;
  }
  .ss-step-cta::before { content: '→'; }

  /* ── TESTIMONIALS ── */
  .ss-testi-grid {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 16px; margin-top: 64px;
  }
  .ss-testi {
    background: var(--bg1); border: 1px solid var(--border); border-radius: 16px;
    overflow: hidden; display: flex; flex-direction: column;
    transition: border-color .3s, transform .3s cubic-bezier(.16,1,.3,1), box-shadow .3s;
  }
  .ss-testi:hover {
    border-color: rgba(255,255,255,0.12); transform: translateY(-6px);
    box-shadow: 0 24px 60px rgba(0,0,0,0.45);
  }
  /* image banner */
  .ss-testi-img {
    width: 100%; height: 160px; position: relative; overflow: hidden; flex-shrink: 0;
  }
  .ss-testi-img img {
    width: 100%; height: 100%; object-fit: cover; object-position: center top;
    transition: transform .55s cubic-bezier(.16,1,.3,1); display: block;
  }
  .ss-testi:hover .ss-testi-img img { transform: scale(1.07); }
  .ss-testi-img::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(7,7,13,0.05) 0%, rgba(7,7,13,0.78) 100%);
  }
  /* quote mark floating on image */
  .ss-testi-quote-mark {
    position: absolute; bottom: 14px; right: 16px; z-index: 2;
    font-family: 'Syne', sans-serif; font-size: 48px; font-weight: 800;
    color: var(--accent); line-height: 1; opacity: 0.9;
  }
  /* body */
  .ss-testi-body { padding: 24px; flex: 1; display: flex; flex-direction: column; }
  .ss-testi-stars { color: var(--accent); font-size: 11px; margin-bottom: 14px; letter-spacing: 3px; }
  .ss-testi-text { font-size: 13.5px; color: var(--muted); line-height: 1.75; margin-bottom: 20px; flex: 1; }
  .ss-testi-author { display: flex; align-items: center; gap: 12px; }
  .ss-testi-avatar {
    width: 40px; height: 40px; border-radius: 50%; overflow: hidden;
    border: 2px solid var(--border); flex-shrink: 0;
  }
  .ss-testi-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .ss-testi-name { font-size: 13px; font-weight: 600; color: var(--text); }
  .ss-testi-meta { font-size: 10px; color: var(--muted); font-family: 'JetBrains Mono', monospace; margin-top: 2px; }

  /* ── CTA ── */
  .ss-cta {
    padding: 120px 48px; position: relative; overflow: hidden;
    border-top: 1px solid var(--border);
  }
  .ss-cta-glow {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
    width: 800px; height: 600px;
    background: radial-gradient(ellipse, rgba(139,92,246,0.15) 0%, transparent 65%);
    pointer-events: none;
  }
  .ss-cta-inner { max-width: 640px; margin: 0 auto; text-align: center; position: relative; z-index: 1; }
  .ss-cta-h2 {
    font-family: 'Syne', sans-serif; font-size: clamp(40px, 5.5vw, 72px);
    font-weight: 800; letter-spacing: -3px; line-height: 0.95;
    color: var(--text); margin-bottom: 20px;
  }
  .ss-cta-h2 em { font-style: normal; color: var(--accent); }
  .ss-cta-p { font-size: 15px; color: var(--muted); margin-bottom: 40px; line-height: 1.7; }
  .ss-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
  .ss-cta-note { margin-top: 20px; font-size: 11px; color: var(--muted); font-family: 'JetBrains Mono', monospace; letter-spacing: 0.06em; }

  /* ── FOOTER ── */
  .ss-footer {
    background: var(--bg1); border-top: 1px solid var(--border);
    padding: 48px; display: flex; align-items: center; justify-content: space-between;
  }
  .ss-footer-logo {
    font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 800;
    color: var(--text); display: flex; align-items: center; gap: 8px;
  }
  .ss-footer-links { display: flex; gap: 28px; }
  .ss-footer-links a { font-size: 12px; color: var(--muted); text-decoration: none; transition: color .2s; }
  .ss-footer-links a:hover { color: var(--text); }
  .ss-footer-copy { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--muted); }

  /* ── SCROLL REVEAL ── */
  .ss-reveal {
    opacity: 0; transform: translateY(50px);
    transition: opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1);
  }
  .ss-reveal.in { opacity: 1; transform: none; }
  .ss-reveal-delay-1 { transition-delay: .1s; }
  .ss-reveal-delay-2 { transition-delay: .2s; }
  .ss-reveal-delay-3 { transition-delay: .3s; }

  /* ── HORIZONTAL SCROLL SECTION ── */
  .ss-hscroll-outer { overflow: hidden; }
  .ss-hscroll-track {
    display: flex; gap: 16px; padding: 0 48px 0 0;
    transition: transform .5s cubic-bezier(.16,1,.3,1);
  }
  .ss-hscroll-card {
    flex: 0 0 340px; background: var(--bg1); border: 1px solid var(--border);
    border-radius: 14px; padding: 36px; position: relative;
    transition: border-color .25s, transform .25s;
  }
  .ss-hscroll-card:hover { border-color: rgba(255,255,255,0.1); transform: translateY(-4px); }
  .ss-hscroll-label {
    font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--muted);
    margin-bottom: 20px; letter-spacing: 0.1em;
  }
  .ss-hscroll-title {
    font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 800;
    letter-spacing: -0.5px; color: var(--text); margin-bottom: 10px;
  }
  .ss-hscroll-desc { font-size: 13px; color: var(--muted); line-height: 1.7; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .ss-hero-inner { grid-template-columns: 1fr; }
    .ss-hero-terminal { display: none; }
    .ss-features-layout { grid-template-columns: 1fr; }
    .ss-features-nav { border-right: none; border-bottom: 1px solid var(--border); }
    .ss-code-body { grid-template-columns: 1fr; }
    .ss-code-editor { border-right: none; border-bottom: 1px solid var(--border); }
    .ss-band-inner { grid-template-columns: repeat(2, 1fr); gap: 32px; }
    .ss-band-stat { border-right: none; padding: 0; text-align: left; }
    .ss-cards { grid-template-columns: 1fr 1fr; }
    .ss-card-img { height: 160px; }
    .ss-testi-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 768px) {
    .ss-nav { padding: 0 20px; }
    .ss-nav-links { display: none; }
    .ss-section { padding: 80px 20px; }
    .ss-hero { padding: 100px 20px 60px; }
    .ss-cards { grid-template-columns: 1fr; }
    .ss-card-img { height: 160px; }
    .ss-steps { grid-template-columns: 1fr; }
    .ss-step-img { height: 160px; }
    .ss-testi-grid { grid-template-columns: 1fr; }
    .ss-footer { flex-direction: column; gap: 20px; text-align: center; }
    .ss-footer-links { flex-wrap: wrap; justify-content: center; }
    .ss-band-inner { grid-template-columns: 1fr 1fr; }
    .ss-cta { padding: 80px 20px; }
  }
`;

/* ── Particle / node canvas for hero ── */
const HeroCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    let raf, t = 0, w, h, nodes;
    const resize = () => {
      w = c.width = c.offsetWidth;
      h = c.height = c.offsetHeight;
      nodes = Array.from({ length: 55 }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - .5) * 0.45,
        vy: (Math.random() - .5) * 0.45,
        r: 1.5 + Math.random() * 2.5,
        hue: Math.random() > 0.7 ? 'accent' : 'dim',
      }));
    };
    resize();
    window.addEventListener('resize', resize);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      t += 0.008;
      nodes.forEach(n => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 100) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(139,92,246,${0.18 * (1 - d / 100)})`;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }
      nodes.forEach(n => {
        const pulse = Math.sin(t * 2 + n.x * 0.01) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * (0.7 + pulse * 0.6), 0, Math.PI * 2);
        ctx.fillStyle = n.hue === 'accent'
          ? `rgba(232,255,71,${0.35 + pulse * 0.4})`
          : `rgba(139,92,246,${0.2 + pulse * 0.3})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
};

/* ── Orbit canvas for feature panel ── */
const OrbitCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    let raf, t = 0;
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    const draw = () => {
      const w = c.width, h = c.height, cx = w / 2, cy = h / 2;
      ctx.clearRect(0, 0, w, h);
      t += 0.02;
      const rings = [
        { r: Math.min(w, h) * 0.37, spd: 0.7, size: 9, col: '#e8ff47', trail: 'rgba(232,255,71,' },
        { r: Math.min(w, h) * 0.24, spd: -1.3, size: 6, col: '#8b5cf6', trail: 'rgba(139,92,246,' },
        { r: Math.min(w, h) * 0.14, spd: 2.2, size: 5, col: '#22d3ee', trail: 'rgba(34,211,238,' },
      ];
      rings.forEach(o => {
        ctx.beginPath(); ctx.arc(cx, cy, o.r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1; ctx.stroke();
        // trail
        for (let i = 0; i < 24; i++) {
          const angle = t * o.spd - i * 0.18;
          const tx = cx + Math.cos(angle) * o.r, ty = cy + Math.sin(angle) * o.r;
          ctx.beginPath(); ctx.arc(tx, ty, o.size * (1 - i / 24) * 0.6, 0, Math.PI * 2);
          ctx.fillStyle = `${o.trail}${(1 - i / 24) * 0.25})`; ctx.fill();
        }
        const ox = cx + Math.cos(t * o.spd) * o.r, oy = cy + Math.sin(t * o.spd) * o.r;
        ctx.beginPath(); ctx.arc(ox, oy, o.size, 0, Math.PI * 2);
        ctx.fillStyle = o.col; ctx.fill();
        ctx.beginPath(); ctx.arc(ox, oy, o.size * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = `${o.trail}0.12)`; ctx.fill();
      });
      ctx.beginPath(); ctx.arc(cx, cy, 14, 0, Math.PI * 2);
      ctx.fillStyle = '#fff'; ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, 8, 0, Math.PI * 2);
      ctx.fillStyle = '#e8ff47'; ctx.fill();
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
};

/* ── Wave bars ── */
const WaveCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let raf, t = 0;
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    const draw = () => {
      const w = c.width, h = c.height; ctx.clearRect(0, 0, w, h); t += 0.045;
      const bars = 32, bw = (w * 0.88) / bars, sx = w * 0.06;
      for (let i = 0; i < bars; i++) {
        const amp = h * 0.78;
        const bh = amp * (0.08 + 0.92 * Math.abs(Math.sin(t * 0.8 + i * 0.38)));
        const x = sx + i * bw, y = h - bh - 4;
        const prog = i / bars;
        const r = Math.floor(139 + (232 - 139) * prog);
        const g = Math.floor(92 + (255 - 92) * prog);
        const bl = Math.floor(246 + (71 - 246) * prog);
        ctx.fillStyle = `rgba(${r},${g},${bl},0.85)`;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(x + 1, y, bw - 2, bh, [3, 3, 0, 0]);
        else ctx.rect(x + 1, y, bw - 2, bh);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
};

/* ── Grid canvas ── */
const GridCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let raf, t = 0;
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    const draw = () => {
      const w = c.width, h = c.height; ctx.clearRect(0, 0, w, h); t += 0.02;
      const cols = 10, rows = 8, cw = w / cols, ch = h / rows;
      for (let r = 0; r < rows; r++) {
        for (let cc = 0; cc < cols; cc++) {
          const wave = Math.sin(t + r * 0.5 + cc * 0.5);
          const scale = 0.1 + 0.5 * ((wave + 1) / 2);
          const cx2 = cw * cc + cw / 2, cy2 = ch * r + ch / 2;
          const bright = (wave + 1) / 2;
          ctx.fillStyle = bright > 0.6
            ? `rgba(232,255,71,${scale * 0.8})`
            : `rgba(139,92,246,${scale * 0.6})`;
          ctx.beginPath(); ctx.arc(cx2, cy2, Math.max(2, scale * 10), 0, Math.PI * 2);
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
};

/* ── Typing animation hook ── */
const useTyping = (texts) => {
  const ref = useRef(null);
  useEffect(() => {
    let ti = 0, i = 0, deleting = false, timer;
    const tick = () => {
      const el = ref.current; if (!el) return;
      const cur = texts[ti];
      el.textContent = deleting ? cur.substring(0, i - 1) : cur.substring(0, i + 1);
      deleting ? i-- : i++;
      let spd = deleting ? 40 : 80;
      if (!deleting && i === cur.length) { spd = 1800; deleting = true; }
      else if (deleting && i === 0) { deleting = false; ti = (ti + 1) % texts.length; spd = 400; }
      timer = setTimeout(tick, spd);
    };
    tick();
    return () => clearTimeout(timer);
  }, []);
  return ref;
};

/* ── Counter hook ── */
const useCounter = (target, decimals = 0, delay = 600) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const steps = 60, step = target / steps;
      let cur = 0;
      const iv = setInterval(() => {
        cur = Math.min(cur + step, target);
        setVal(decimals > 0 ? parseFloat(cur.toFixed(decimals)) : Math.floor(cur));
        if (cur >= target) clearInterval(iv);
      }, 1800 / steps);
    }, delay);
    return () => clearTimeout(t);
  }, []);
  return val;
};

const FEATURES = [
  {
    n: '01', icon: '🧠', title: 'AI Boss Battles',
    desc: 'Adaptive challenges that evolve with your exact skill level. Every session is different. Every battle pushes you further.',
    tag: '// reinforcement learning',
    canvas: 'orbit',
  },
  {
    n: '02', icon: '📊', title: 'Live XP Dashboard',
    desc: 'Visual mastery tracking with real-time XP accumulation, streak detection, and weak-zone heatmaps.',
    tag: '// stagger animation',
    canvas: 'wave',
  },
  {
    n: '03', icon: '🎯', title: 'Weakness Crusher',
    desc: 'Smart targeting locks onto your hardest problem areas and generates precision practice until they crack.',
    tag: '// grid physics',
    canvas: 'grid',
  },
  {
    n: '04', icon: '🏆', title: 'Global Leaderboard',
    desc: 'Compete with students across India. Real rankings. Real pressure. Real results.',
    tag: '// competitive mode',
    canvas: 'orbit',
  },
];

export default function Welcome() {
  const navigate = useNavigate();
  const go = (path) => setTimeout(() => navigate(path), 180);

  const [progress, setProgress] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const typedRef = useTyping(['Level Up Math.', 'Master Calculus.', 'Crush Exams.', 'Unlock Potential.']);
  const students = useCounter(10000, 0, 800);
  const successRate = useCounter(95, 0, 1000);
  const rating = useCounter(4.9, 1, 1200);

  // Scroll progress
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const prog = el.scrollTop / (el.scrollHeight - el.clientHeight);
      setProgress(Math.min(1, prog) * 100);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll('.ss-reveal');
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.08 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Auto-cycle features
  useEffect(() => {
    const iv = setInterval(() => setActiveFeature(a => (a + 1) % FEATURES.length), 3000);
    return () => clearInterval(iv);
  }, []);

  const FeatureCanvas = ({ type }) => {
    if (type === 'orbit') return <OrbitCanvas />;
    if (type === 'wave') return <WaveCanvas />;
    if (type === 'grid') return <GridCanvas />;
    return <OrbitCanvas />;
  };

  return (
    <>
      <style>{styles}</style>
      <div className="ss">

        {/* Progress bar */}
        <div className="ss-progress-bar" style={{ width: `${progress}%` }} />

        {/* NAV */}
        <nav className="ss-nav">
          <div className="ss-nav-logo">
            <div className="ss-nav-logo-mark">S</div>
            SolveSmart
          </div>
          <ul className="ss-nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#how">Process</a></li>
            <li><a href="#demo">Live demo</a></li>
          </ul>
          <div className="ss-nav-right">
            <button className="ss-nav-ghost" onClick={() => go('/login')}>Sign in</button>
            <button className="ss-nav-cta" onClick={() => go('/school-info')}>Start free →</button>
          </div>
        </nav>

        {/* ── HERO ── */}
        <section className="ss-hero">
          <div className="ss-hero-bg">
            <div className="ss-hero-glow-1" />
            <div className="ss-hero-glow-2" />
            <div className="ss-hero-glow-3" />
          </div>
          <div className="ss-hero-inner">
            <div>
              <div className="ss-hero-eyebrow">AI · Math · Gamified learning</div>
              <h1 className="ss-hero-h1">
                <span className="line"><span className="line-inner outline" ref={typedRef}></span></span>
                <span className="line"><span className="line-inner">With Solve</span></span>
                <span className="line"><span className="line-inner accent">Smart.</span></span>
              </h1>
              <p className="ss-hero-sub">
                An AI-powered math platform that adapts to your learning style.
                Built for CBSE &amp; ICSE students, Classes 1–12.
              </p>
              <div className="ss-hero-actions">
                <button className="ss-btn-primary" onClick={() => go('/school-info')}>
                  Start your quest →
                </button>
                <button className="ss-btn-ghost" onClick={() => go('/login')}>
                  Watch demo
                </button>
              </div>
              <div className="ss-hero-stats">
                <div className="ss-stat-item">
                  <div className="ss-stat-num">{students.toLocaleString()}<em>+</em></div>
                  <div className="ss-stat-label">Active students</div>
                </div>
                <div className="ss-stat-item">
                  <div className="ss-stat-num">{successRate}<em>%</em></div>
                  <div className="ss-stat-label">Victory rate</div>
                </div>
                <div className="ss-stat-item">
                  <div className="ss-stat-num">{rating}<em>★</em></div>
                  <div className="ss-stat-label">Avg rating</div>
                </div>
              </div>
            </div>

            {/* Terminal card */}
            <div className="ss-hero-terminal">
              <div className="ss-terminal-bar">
                <div className="ss-t-dot" style={{ background: '#ff5f57' }} />
                <div className="ss-t-dot" style={{ background: '#febc2e' }} />
                <div className="ss-t-dot" style={{ background: '#28c840' }} />
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--muted)', marginLeft: 12 }}>
                  solvesmart — adaptive engine
                </span>
              </div>
              <div className="ss-terminal-body">
                <div className="ss-t-line"><span className="ss-t-prompt">▸</span><span className="ss-t-cmd">import</span>&nbsp;<span className="ss-t-arg">SolveSmart</span>&nbsp;<span style={{color:'var(--muted)'}}>from</span>&nbsp;<span style={{color:'#a8ff78'}}>'@solvesmart/core'</span></div>
                <div style={{height:8}}/>
                <div className="ss-t-line"><span className="ss-t-prompt">▸</span><span className="ss-t-cmd">const</span>&nbsp;<span className="ss-t-arg">student</span>&nbsp;= SolveSmart.<span style={{color:'var(--cyan)'}}>init</span>({'{'}</div>
                <div className="ss-t-line" style={{paddingLeft:20}}><span style={{color:'var(--purple)'}}>grade</span>: <span style={{color:'#ffcc66'}}>10</span>,</div>
                <div className="ss-t-line" style={{paddingLeft:20}}><span style={{color:'var(--purple)'}}>board</span>: <span style={{color:'#a8ff78'}}>'CBSE'</span>,</div>
                <div className="ss-t-line" style={{paddingLeft:20}}><span style={{color:'var(--purple)'}}>mode</span>: <span style={{color:'#a8ff78'}}>'adaptive'</span>,</div>
                <div className="ss-t-line"><span style={{color:'var(--muted)'}}>{'}'});</span></div>
                <div style={{height:8}}/>
                <div className="ss-t-out" style={{color:'#4ade80'}}>✓ AI model loaded (94ms)</div>
                <div className="ss-t-out" style={{color:'#4ade80'}}>✓ Student profile calibrated</div>
                <div className="ss-t-out" style={{color:'#4ade80'}}>✓ Adaptive challenges ready</div>
                <div style={{height:8}}/>
                <div className="ss-t-line">
                  <span className="ss-t-prompt">▸</span>
                  <span className="ss-t-cursor" />
                </div>
              </div>
              <div style={{ padding: '0 0 0', borderTop: '1px solid var(--border)', height: 180 }}>
                <HeroCanvas />
              </div>
            </div>
          </div>
        </section>

        {/* ── MARQUEE ── */}
        <div className="ss-marquee-wrap">
          <div className="ss-marquee-track">
            {['CBSE Classes 1-12','ICSE Compatible','AI Adaptive Engine','Real-time Analytics','Gamified XP System','10K+ Students','Ranked Battles','Streak Rewards','Weak Zone Detection','Boss Fight Mode'].concat(
              ['CBSE Classes 1-12','ICSE Compatible','AI Adaptive Engine','Real-time Analytics','Gamified XP System','10K+ Students','Ranked Battles','Streak Rewards','Weak Zone Detection','Boss Fight Mode']
            ).map((t, i) => (
              <span className="ss-marquee-item" key={i}>{t}</span>
            ))}
          </div>
        </div>

        {/* ── FEATURES ── */}
        <section className="ss-section" id="features" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="ss-container">
            <div className="ss-reveal">
              <div className="ss-eyebrow">// power-ups</div>
              <h2 className="ss-h2">Your math arsenal.</h2>
              <p className="ss-lead">Unlock tools that adapt, predict, and push you further every session.</p>
            </div>
            <div className="ss-reveal" style={{ marginTop: 64 }}>
              <div className="ss-features-layout">
                <div className="ss-features-nav">
                  {FEATURES.map((f, i) => (
                    <div
                      key={f.n}
                      className={`ss-feat-tab${activeFeature === i ? ' active' : ''}`}
                      onClick={() => setActiveFeature(i)}
                    >
                      <div className="ss-feat-tab-num">{f.n}</div>
                      <div className="ss-feat-tab-title">{f.title}</div>
                    </div>
                  ))}
                </div>
                {FEATURES.map((f, i) => (
                  <div key={f.n} className={`ss-feat-panel${activeFeature === i ? ' active' : ''}`}>
                    <span className="ss-feat-icon">{f.icon}</span>
                    <div className="ss-feat-title">{f.title}</div>
                    <div className="ss-feat-desc">{f.desc}</div>
                    <div className="ss-feat-tag">
                      <span>◉</span>{f.tag}
                    </div>
                    <div className="ss-feat-canvas">
                      <FeatureCanvas type={f.canvas} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── CARDS GRID ── */}
        <section className="ss-section" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="ss-container">
            <div className="ss-reveal">
              <div className="ss-eyebrow">// capabilities</div>
              <h2 className="ss-h2">Built different.</h2>
            </div>
            <div className="ss-cards">
              {[
                {
                  n: '01', icon: '⚡', title: 'Instant Feedback',
                  desc: 'Step-by-step AI explanations in milliseconds. Never be stuck on a problem again.',
                  tag: 'AI explanations',
                  img: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?w=600&q=80',
                },
                {
                  n: '02', icon: '🎮', title: 'Gamified Practice',
                  desc: 'XP, streaks, boss battles, daily quests. Math practice that actually feels like a game.',
                  tag: 'Game mechanics',
                  img: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&q=80',
                },
                {
                  n: '03', icon: '📱', title: 'Works Everywhere',
                  desc: 'Desktop, tablet, phone — your progress and streaks sync across every device in real-time.',
                  tag: 'Cross-platform',
                  img: 'https://images.unsplash.com/photo-1526925539332-aa3b66e35444?w=600&q=80',
                },
                {
                  n: '04', icon: '🗺️', title: 'CBSE & ICSE Ready',
                  desc: 'Full curriculum coverage for Classes 1–12. Every topic, every chapter, exam-aligned.',
                  tag: 'Full syllabus',
                  img: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=600&q=80',
                },
                {
                  n: '05', icon: '🔥', title: 'Streak Engine',
                  desc: 'Daily streaks and smart habit loops that turn practice into an effortless routine.',
                  tag: 'Habit building',
                  img: 'https://images.unsplash.com/photo-1541963463532-d68292c34b19?w=600&q=80',
                },
                {
                  n: '06', icon: '🧩', title: 'Smart Difficulty',
                  desc: 'Problems auto-calibrate to your level. Never too easy to bore you, never too hard to lose you.',
                  tag: 'Adaptive AI',
                  img: 'https://images.unsplash.com/photo-1596496181848-3091d4878b24?w=600&q=80',
                },
              ].map((c, i) => (
                <div className="ss-card ss-reveal" key={c.n} style={{ transitionDelay: `${(i % 3) * 0.08}s` }}>
                  <div className="ss-card-accent-bar" />
                  <div className="ss-card-img">
                    <img src={c.img} alt={c.title} loading="lazy" />
                    <div className="ss-card-img-badge">{c.icon}</div>
                  </div>
                  <div className="ss-card-body">
                    <div className="ss-card-n">{c.n}</div>
                    <div className="ss-card-title">{c.title}</div>
                    <div className="ss-card-desc">{c.desc}</div>
                    <div className="ss-card-tag">{c.tag}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BIG NUMBERS BAND ── */}
        <div className="ss-band">
          <div className="ss-band-inner">
            {[
              { n: '10K+', label: 'Active students' },
              { n: '95%', label: 'Exam score improvement' },
              { n: '4.9★', label: 'Average rating' },
              { n: '50+', label: 'Topics covered' },
            ].map((s, i) => (
              <div className="ss-band-stat ss-reveal" key={i} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="ss-band-n">{s.n}</div>
                <div className="ss-band-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CODE WINDOW / DEMO ── */}
        <section className="ss-section" id="demo" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="ss-container">
            <div className="ss-reveal">
              <div className="ss-eyebrow">// live demo</div>
              <h2 className="ss-h2">See the engine.</h2>
              <p className="ss-lead">Every practice session is dynamically generated. Watch the adaptive engine in action.</p>
            </div>
            <div className="ss-code-win ss-reveal">
              <div className="ss-code-topbar">
                <div className="ss-t-dot" style={{ background: '#ff5f57' }} />
                <div className="ss-t-dot" style={{ background: '#febc2e' }} />
                <div className="ss-t-dot" style={{ background: '#28c840' }} />
                <span className="ss-code-tab act">adaptive.engine.js</span>
                <span className="ss-code-tab">leaderboard.js</span>
                <span className="ss-code-tab">xp.system.js</span>
              </div>
              <div className="ss-code-body">
                <div className="ss-code-editor">
                  <span className="cm">// Adaptive challenge generator</span><br/>
                  <span className="ck">async function</span> <span className="cf">generateChallenge</span>(<span className="cp">student</span>) {'{'}<br/>
                  &nbsp;&nbsp;<span className="ck">const</span> <span className="cn">profile</span> = <span className="ck">await</span> AI.analyze(<span className="cp">student</span>);<br/>
                  &nbsp;&nbsp;<span className="ck">const</span> <span className="cn">weakZones</span> = profile.getWeakZones();<br/>
                  <br/>
                  &nbsp;&nbsp;<span className="ck">return</span> Challenge.create({'{'}<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="cp">difficulty</span>: profile.optimalLevel,<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="cp">topics</span>: weakZones.top(<span className="cn">3</span>),<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="cp">xpReward</span>: <span className="cf">calcXP</span>(profile),<br/>
                  &nbsp;&nbsp;&nbsp;&nbsp;<span className="cp">adaptive</span>: <span className="ck">true</span>,<br/>
                  &nbsp;&nbsp;{'}'});<br/>
                  {'}'}<br/>
                  <br/>
                  <span className="cm">// Result: always the right challenge</span>
                </div>
                <div className="ss-code-preview">
                  <OrbitCanvas />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="ss-section" id="how" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="ss-container">
            <div className="ss-reveal">
              <div className="ss-eyebrow">// quest path</div>
              <h2 className="ss-h2">3 steps to mastery.</h2>
              <p className="ss-lead">From zero to hero in under 5 minutes. No credit card needed.</p>
            </div>
            <div className="ss-steps ss-reveal">
              {[
                {
                  n: '01', icon: '🦸', title: 'Create your hero',
                  desc: 'Quick signup — choose your grade, board (CBSE/ICSE), and avatar. Done in 60 seconds flat.',
                  cta: 'Sign up free',
                  img: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&q=80',
                  accent: 'linear-gradient(90deg, var(--accent), var(--cyan))',
                },
                {
                  n: '02', icon: '🔬', title: 'Skill diagnostic',
                  desc: 'A short scan builds your personalised challenge campaign. The AI maps every strength and gap instantly.',
                  cta: 'See how it works',
                  img: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600&q=80',
                  accent: 'linear-gradient(90deg, var(--purple), var(--pink))',
                },
                {
                  n: '03', icon: '🚀', title: 'Start your quest',
                  desc: 'Face daily adaptive challenges, earn XP, climb rankings, and watch your score rise every week.',
                  cta: 'Explore challenges',
                  img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&q=80',
                  accent: 'linear-gradient(90deg, var(--accent), var(--purple))',
                },
              ].map((s, i) => (
                <div className="ss-step ss-reveal" key={s.n} style={{ transitionDelay: `${i * 0.1}s` }}>
                  <div className="ss-step-accent-bar" style={{ background: s.accent }} />
                  <div className="ss-step-img">
                    <img src={s.img} alt={s.title} loading="lazy" />
                    <div className="ss-step-badge">{s.n}</div>
                    <div className="ss-step-img-icon">{s.icon}</div>
                  </div>
                  <div className="ss-step-body">
                    <div className="ss-step-title">{s.title}</div>
                    <div className="ss-step-desc">{s.desc}</div>
                    <div className="ss-step-cta">{s.cta}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── TESTIMONIALS ── */}
        <section className="ss-section" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="ss-container">
            <div className="ss-reveal">
              <div className="ss-eyebrow">// player reviews</div>
              <h2 className="ss-h2">Students winning.</h2>
            </div>
            <div className="ss-testi-grid">
              {[
                {
                  stars: '★★★★★',
                  text: 'My maths marks went from 62 to 91 in one term. The boss battle mode makes practice actually fun.',
                  name: 'Aarav S.', meta: 'Class 10 · CBSE · Mumbai',
                  img: 'https://images.unsplash.com/photo-1588072432836-e10032774350?w=600&q=80',
                  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
                },
                {
                  stars: '★★★★★',
                  text: 'The adaptive questions are on point — it somehow always knows exactly where I\'m struggling.',
                  name: 'Priya K.', meta: 'Class 12 · ICSE · Bangalore',
                  img: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&q=80',
                  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80',
                },
                {
                  stars: '★★★★★',
                  text: 'Streak system got me solving problems every single day. 45-day streak and still going!',
                  name: 'Rohan M.', meta: 'Class 9 · CBSE · Delhi',
                  img: 'https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=600&q=80',
                  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80',
                },
              ].map((t, i) => (
                <div className="ss-testi ss-reveal" key={i} style={{ transitionDelay: `${i * 0.12}s` }}>
                  <div className="ss-testi-img">
                    <img src={t.img} alt={t.name} loading="lazy" />
                    <div className="ss-testi-quote-mark">"</div>
                  </div>
                  <div className="ss-testi-body">
                    <div className="ss-testi-stars">{t.stars}</div>
                    <div className="ss-testi-text">{t.text}</div>
                    <div className="ss-testi-author">
                      <div className="ss-testi-avatar">
                        <img src={t.avatar} alt={t.name} loading="lazy" />
                      </div>
                      <div>
                        <div className="ss-testi-name">{t.name}</div>
                        <div className="ss-testi-meta">{t.meta}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="ss-cta">
          <div className="ss-cta-glow" />
          <div className="ss-cta-inner ss-reveal">
            <div className="ss-eyebrow" style={{ justifyContent: 'center' }}>// final boss</div>
            <h2 className="ss-cta-h2">Ready to conquer <em>math?</em></h2>
            <p className="ss-cta-p">
              Join 10,000+ students already dominating with SolveSmart. Free to start. No card needed.
            </p>
            <div className="ss-cta-btns">
              <button className="ss-btn-primary" style={{ fontSize: 13, padding: '16px 36px' }} onClick={() => go('/school-info')}>
                Start free — no card needed →
              </button>
              <button className="ss-btn-ghost" style={{ fontSize: 13, padding: '16px 28px' }} onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>
                Explore features
              </button>
            </div>
            <p className="ss-cta-note">FREE TO START · CBSE &amp; ICSE CLASS 1–12 · CANCEL ANYTIME</p>
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer className="ss-footer">
          <div className="ss-footer-logo">
            <div className="ss-nav-logo-mark" style={{ width: 22, height: 22, fontSize: 12, borderRadius: 5 }}>S</div>
            SolveSmart
          </div>
          <div className="ss-footer-links">
            <a href="#">Features</a>
            <a href="#">How it works</a>
            <a href="#">About</a>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
          </div>
          <div className="ss-footer-copy">© 2025 SolveSmart · Made in India 🇮🇳</div>
        </footer>

      </div>
    </>
  );
}